// Master Apps · Acervo de documentos — uma linha por aplicação; clicar na linha
// expande, na própria tabela, a subtabela dos artefatos (link + situação) com a
// conformidade daquela aplicação. Regra de conformidade: @/lib/compliance.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppPhoto, AppTypeTag } from '@/components/app-visuals';
import { DocDownloadButton } from '@/components/doc-download-button';
import {
  IconAlert,
  IconCheck,
  IconChevronR,
  IconExternal,
  IconSearch,
  IconX,
} from '@/components/icons';
import { ButtonGhost, FilterChip, IconDoc, Sidebar, TopBar } from '@/components/primitives';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import type { App } from '@/lib/catalog-config';
import type { ComplianceCheck, ComplianceLevel, ComplianceResult, DocKey } from '@/lib/compliance';
import {
  COMPLIANCE_GREEN,
  COMPLIANCE_LEVEL_LABELS,
  COMPLIANCE_RED,
  complianceColor,
  complianceLevelColor,
  complianceOf,
  complianceTone,
  DOC_KEYS,
  docsFilled,
} from '@/lib/compliance';
import { DOC_FIELD_BY_KEY } from '@/lib/document-download';
import type { ScreenProps } from '@/lib/theme';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  rotuloCaixaAlta,
} from '@/lib/theme';

type Role = 'admin' | 'collab';
type LevelFilter = 'all' | ComplianceLevel;
type GapFilter = 'all' | 'any' | DocKey;
type SortMode = 'coverage' | 'name';

/** Colunas da tabela principal — cabeçalho e linhas usam a mesma grade. */
const GRID = '2.3fr 1fr 1fr 0.9fr 180px 34px';

/**
 * Largura mínima do canvas da tabela no TABLET, onde ela ganha rolagem
 * horizontal deliberada em vez de comprimir as 6 colunas. 1080px é o que faz a
 * coluna do responsável ficar com ~147px — acima do que "PAULO ARRUDA GOMES"
 * precisa (129px, medido no navegador). Abaixo disso o nome voltaria a sumir em
 * reticências, que é exatamente o defeito que a rolagem existe para evitar.
 */
const TABLE_MIN_WIDTH = 1080;

/**
 * Recorte de texto por faixa. No desktop a célula tem largura fixa e corta com
 * reticências; no card do celular o texto QUEBRA, porque reticências exigem
 * `nowrap` e aí o responsável e o slug desapareciam por completo. `anywhere`
 * (e não `break-word`) porque slug e URL são "palavras" longas sem espaço.
 */
const CLIP_TEXT: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const WRAP_TEXT: React.CSSProperties = { overflowWrap: 'anywhere' };

/** Rolagem lateral deliberada (tablet) x recorte com canto arredondado (resto). */
const TABLE_SCROLL: React.CSSProperties = { overflowX: 'auto', overflowY: 'hidden' };
const TABLE_CLIP: React.CSSProperties = { overflow: 'hidden' };

/**
 * Geometria da LINHA da tabela por faixa. Um objeto por faixa, e não um ternário
 * em cada propriedade do JSX: eram mais de dez decisões dentro do mesmo
 * componente, o suficiente para estourar o teto de complexidade cognitiva.
 * `slot` posiciona cada célula; no desktop fica vazio e a grade de 6 colunas
 * segue o fluxo natural, exatamente como antes.
 */
interface RowLayout {
  columns: string;
  gap: number;
  padding: string;
  align: React.CSSProperties['alignItems'];
  slot: Record<'app' | 'type' | 'owner' | 'team' | 'coverage' | 'chevron', React.CSSProperties>;
  text: React.CSSProperties;
}

const ROW_WIDE: RowLayout = {
  columns: GRID,
  gap: 14,
  padding: '12px 16px',
  align: 'center',
  slot: { app: {}, type: {}, owner: {}, team: {}, coverage: {}, chevron: {} },
  text: CLIP_TEXT,
};

/**
 * Card do celular: nome + seta na 1ª linha, tipo na 2ª, responsável e time lado
 * a lado na 3ª e a cobertura inteira na 4ª. As colunas são `minmax(0, 1fr)`
 * porque `1fr` puro tem piso de conteúdo — com ele a linha esticava 115px além
 * da tela em 320px (medido). A 3ª coluna de 22px é só a seta.
 */
const ROW_NARROW: RowLayout = {
  columns: 'minmax(0, 1fr) minmax(0, 1fr) 22px',
  gap: 8,
  padding: '12px',
  align: 'start',
  slot: {
    app: { gridColumn: '1 / span 2', gridRow: 1 },
    chevron: { gridColumn: 3, gridRow: 1 },
    type: { gridColumn: '1 / span 3', gridRow: 2 },
    owner: { gridColumn: 1, gridRow: 3 },
    team: { gridColumn: 2, gridRow: 3 },
    coverage: { gridColumn: '1 / span 3', gridRow: 4 },
  },
  text: WRAP_TEXT,
};

/** Geometria da SUBTABELA de documentos, mesma ideia de um objeto por faixa. */
interface DocLayout {
  columns: string;
  gap: number;
  padding: string;
  slot: Record<'label' | 'link' | 'status', React.CSSProperties>;
  text: React.CSSProperties;
  linkAlign: React.CSSProperties['alignItems'];
  linkOverflow: React.CSSProperties['overflow'];
  iconTop: number;
  /**
   * Alvo de toque do link do documento — o único jeito de ABRIR o artefato, e o
   * mais errado com o dedo: uma linha de texto de 11,5px tem 17px de altura.
   * `undefined` no desktop (a propriedade não é emitida) porque lá o ponteiro
   * acerta os 17px e a linha da subtabela não pode crescer.
   */
  linkMinHeight: number | undefined;
}

const DOC_WIDE: DocLayout = {
  columns: '230px 1fr 140px',
  gap: 14,
  padding: '10px 14px',
  slot: { label: {}, link: {}, status: {} },
  text: CLIP_TEXT,
  linkAlign: 'center',
  linkOverflow: 'hidden',
  iconTop: 0,
  linkMinHeight: undefined,
};

/** No celular: rótulo e selo na mesma linha, link na linha de baixo inteira. */
const DOC_NARROW: DocLayout = {
  columns: '1fr auto',
  gap: 8,
  padding: '12px',
  slot: {
    label: { gridColumn: 1, gridRow: 1 },
    status: { gridColumn: 2, gridRow: 1 },
    link: { gridColumn: '1 / span 2', gridRow: 2 },
  },
  text: WRAP_TEXT,
  // Com o link quebrado em várias linhas o ícone tem de acompanhar a PRIMEIRA
  // delas; centralizado, ele descia para o meio do bloco.
  linkAlign: 'flex-start',
  linkOverflow: 'visible',
  iconTop: 1,
  linkMinHeight: 40,
};

/** Geometria do BLOCO EXPANDIDO (cabeçalho, painéis e critérios de cadastro). */
interface ExpandedLayout {
  padding: string;
  headDirection: React.CSSProperties['flexDirection'];
  headAlign: React.CSSProperties['alignItems'];
  headGap: number;
  titleWrap: React.CSSProperties['overflowWrap'];
  panels: string;
  metaDirection: React.CSSProperties['flexDirection'];
  metaAlign: React.CSSProperties['alignItems'];
  metaGap: number;
  text: React.CSSProperties;
}

const EXPANDED_WIDE: ExpandedLayout = {
  // O recuo de 58px alinha o bloco com o nome da aplicação na linha acima.
  padding: '14px 16px 18px 58px',
  headDirection: 'row',
  headAlign: 'center',
  headGap: 12,
  titleWrap: 'normal',
  panels: '1fr 1fr',
  metaDirection: 'row',
  metaAlign: 'center',
  metaGap: 10,
  text: CLIP_TEXT,
};

/**
 * No celular o bloco encosta na borda (58px de recuo comeriam 20% de uma tela de
 * 320px) e tudo empilha: o valor de um critério é URL de repositório, e ao lado
 * de um selo de ~110px num painel de 244px ele virava apenas "https://…".
 */
const EXPANDED_NARROW: ExpandedLayout = {
  padding: '12px 10px 14px',
  headDirection: 'column',
  headAlign: 'flex-start',
  headGap: 8,
  titleWrap: 'anywhere',
  panels: '1fr',
  metaDirection: 'column',
  metaAlign: 'flex-start',
  metaGap: 4,
  text: WRAP_TEXT,
};

const GAP_CHIPS: { id: GapFilter; label: string }[] = [
  { id: 'any', label: 'Com pendências' },
  { id: 'tech', label: 'Sem doc técnica' },
  { id: 'biz', label: 'Sem regra de negócio' },
  { id: 'api', label: 'Sem doc de API' },
  { id: 'req', label: 'Sem requisitos' },
  { id: 'repo', label: 'Sem repositório' },
];

/** href seguro: links cadastrados sem protocolo abririam como caminho relativo. */
function hrefOf(raw: string): string {
  const value = raw.trim();
  return /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;
}

/**
 * Corta as barras finais em tempo LINEAR. O `replace(/\/+$/, '')` que estava aqui
 * é super-linear: o `+` é guloso e o motor reinicia a tentativa em cada barra de
 * uma sequência, então um caminho terminado em muitas barras (`/a////…////`)
 * custa O(n²) — e o caminho vem de link cadastrado pelo usuário.
 */
function withoutTrailingSlashes(path: string): string {
  let end = path.length;
  while (end > 0 && path[end - 1] === '/') end -= 1;
  return path.slice(0, end);
}

/** Exibe host + início do caminho; sem parse possível, encurta a string crua. */
function shortUrl(raw: string): string {
  const value = raw.trim();
  try {
    const u = new URL(hrefOf(value));
    const path = withoutTrailingSlashes(u.pathname);
    const tail = path.length > 26 ? `${path.slice(0, 26)}…` : path;
    return `${u.host}${tail}`;
  } catch {
    return value.length > 46 ? `${value.slice(0, 46)}…` : value;
  }
}

function Bar({
  value,
  total,
  color,
  dark,
  height = 6,
}: Readonly<{
  value: number;
  total: number;
  color: string;
  dark: boolean;
  height?: number;
}>) {
  return (
    <div
      style={{
        height,
        background: dark ? '#26272d' : '#f1f5f4',
        borderRadius: height / 2,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${total > 0 ? (value / total) * 100 : 0}%`,
          height: '100%',
          background: color,
          transition: 'width 400ms',
        }}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  color,
  icon,
  dark,
}: Readonly<{
  label: string;
  value: string;
  sub: string;
  color: string;
  icon: React.ReactNode;
  dark: boolean;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 8,
        padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 18 }),
        // Em 2 colunas de 320px a coluna tem ~143px: sem `minWidth: 0` o piso de
        // conteúdo do tile esticaria a grade e a fileira estouraria a tela (era
        // o estouro de 631px medido no acervo a 320px).
        minWidth: vp.compact ? 0 : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: byViewport(vp, { mobile: 9.5, tablet: 10, desktop: 10 }),
          fontWeight: 700,
          color: p.muted,
          textTransform: 'uppercase',
          // Menos espaçamento entre letras no celular: "Documentação completa"
          // com 0.1em não caberia em duas linhas de 115px.
          letterSpacing: byViewport(vp, { mobile: '0.06em', tablet: '0.1em', desktop: '0.1em' }),
          marginBottom: byViewport(vp, { mobile: 8, tablet: 10, desktop: 10 }),
          overflowWrap: vp.compact ? 'anywhere' : 'normal',
        }}
      >
        <span style={{ color, display: 'flex', flexShrink: 0 }}>{icon}</span>
        {label}
      </div>
      <div
        style={{
          ...numeroTabular,
          fontSize: byViewport(vp, { mobile: 24, tablet: 30, desktop: 34 }),
          fontWeight: 600,
          color: p.text,
          letterSpacing: '-0.025em',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          color: p.muted,
          marginTop: 6,
          overflowWrap: vp.compact ? 'anywhere' : 'normal',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

/**
 * Rótulo e cor da situação de um critério, na ordem em que a auditoria decide:
 * link cadastrado ganha do resto, depois a dispensa, e o que sobra é ausência.
 * `mutedColor` entra por parâmetro porque a dispensa usa o cinza da paleta.
 */
function statusPillTone(
  check: ComplianceCheck,
  mutedColor: string,
): { label: string; color: string } {
  if (check.value) return { label: 'Cadastrado', color: COMPLIANCE_GREEN };
  if (check.exempt) return { label: 'Dispensado', color: mutedColor };
  return { label: 'Não cadastrado', color: COMPLIANCE_RED };
}

/** Ícone da situação: check para cadastrado, X para ausente, nada na dispensa. */
function statusPillIcon(check: ComplianceCheck): React.ReactNode {
  if (check.value) return <IconCheck size={11} />;
  if (check.exempt) return null;
  return <IconX size={11} />;
}

/** Situação de um critério: cadastrado, dispensado por isenção ou ausente. */
function StatusPill({ check, dark }: Readonly<{ check: ComplianceCheck; dark: boolean }>) {
  const p = makePalette(dark);
  const filled = !!check.value;
  const tone = statusPillTone(check, p.muted);
  const t = complianceTone(tone.color, dark);
  return (
    <span
      style={{
        ...rotuloCaixaAlta,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 8px',
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        background: filled || !check.exempt ? t.bg : p.surface2,
        color: tone.color,
        border: `1px solid ${filled || !check.exempt ? t.border : p.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {statusPillIcon(check)}
      {tone.label}
    </span>
  );
}

function LevelBadge({ level, dark }: Readonly<{ level: ComplianceLevel; dark: boolean }>) {
  const color = complianceLevelColor(level);
  const t = complianceTone(color, dark);
  return (
    <span
      style={{
        ...rotuloCaixaAlta,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 8px',
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        background: t.bg,
        color,
        border: `1px solid ${t.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {COMPLIANCE_LEVEL_LABELS[level]}
    </span>
  );
}

/** Linha da subtabela: rótulo do artefato, link clicável e situação. */
function DocRow({
  check,
  dark,
  accent,
  last,
  applicationId,
}: Readonly<{
  check: ComplianceCheck;
  dark: boolean;
  accent: string;
  last: boolean;
  /** Habilita o download via servidor (a tela inteira é AdminOnly). */
  applicationId: string;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // Reticências só funcionam onde a coluna tem largura fixa. Em 320px o link já
  // encurtado por `shortUrl` ainda pede ~317px e viraria só "…", escondendo o
  // documento: no celular ele quebra em duas linhas e continua legível.
  const L = vp.mobile ? DOC_NARROW : DOC_WIDE;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: L.columns,
        gap: L.gap,
        alignItems: 'center',
        padding: L.padding,
        borderBottom: last ? 'none' : `1px solid ${p.border}`,
      }}
    >
      <div
        style={{
          ...L.slot.label,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12.5,
          fontWeight: 500,
          color: p.text,
          minWidth: 0,
        }}
      >
        <span style={{ color: p.muted, display: 'flex', flexShrink: 0 }}>
          <IconDoc size={13} />
        </span>
        <span style={L.text}>{check.label}</span>
      </div>
      <div style={{ ...L.slot.link, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        {check.value ? (
          <>
            <a
              href={hrefOf(check.value)}
              target="_blank"
              rel="noreferrer"
              title={check.value}
              style={{
                display: 'inline-flex',
                alignItems: L.linkAlign,
                gap: 6,
                maxWidth: '100%',
                minWidth: 0,
                // No celular a linha do link vira o alvo de 40px de altura (ver
                // `linkMinHeight`); `maxWidth: 100%` continua sendo o que impede o
                // link comprido de empurrar a largura do card.
                minHeight: L.linkMinHeight,
                fontSize: 11.5,
                fontFamily: fontMono,
                color: accent,
                textDecoration: 'none',
                overflow: L.linkOverflow,
              }}
            >
              <span style={{ display: 'flex', flexShrink: 0, paddingTop: L.iconTop }}>
                <IconExternal size={12} />
              </span>
              <span style={L.text}>{shortUrl(check.value)}</span>
            </a>
            {/* Baixar via servidor — irmão da âncora (nunca dentro dela) e só
                aparece quando a fonte tem download direto (GitHub/SharePoint).
                A tela é AdminOnly: todo mundo aqui pode baixar. */}
            {check.key in DOC_FIELD_BY_KEY && (
              <DocDownloadButton
                applicationId={applicationId}
                field={DOC_FIELD_BY_KEY[check.key as DocKey]}
                url={check.value}
                dark={dark}
                accent={accent}
                size={24}
              />
            )}
          </>
        ) : (
          <span style={{ fontSize: 11.5, color: p.placeholder }}>
            {check.exempt ? 'Não exigido para esta aplicação' : 'Nenhum link cadastrado'}
          </span>
        )}
      </div>
      <div style={{ ...L.slot.status, display: 'flex', justifyContent: 'flex-end' }}>
        <StatusPill check={check} dark={dark} />
      </div>
    </div>
  );
}

/** Bloco expandido de uma aplicação: subtabela + cadastro + conformidade. */
function ExpandedRow({
  app,
  result,
  dark,
  accent,
  onOpenApp,
}: Readonly<{
  app: App;
  result: ComplianceResult;
  dark: boolean;
  accent: string;
  onOpenApp: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // No TABLET o bloco expandido vive dentro do canvas de 1080px da tabela, então
  // continua com a largura do desktop: só o celular precisa de outro formato.
  const narrow = vp.mobile;
  const L = narrow ? EXPANDED_NARROW : EXPANDED_WIDE;
  const docChecks = result.checks.filter((c) => c.kind === 'doc');
  const metaChecks = result.checks.filter((c) => c.kind === 'meta');
  const color = complianceColor(result.pct);
  const gapTone = complianceTone(COMPLIANCE_RED, dark);

  return (
    <div
      style={{
        padding: L.padding,
        background: dark ? '#0e0f12' : '#fbfbfa',
        borderTop: `1px dashed ${p.border}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: L.headAlign,
          justifyContent: 'space-between',
          flexDirection: L.headDirection,
          gap: L.headGap,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: p.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            overflowWrap: L.titleWrap,
          }}
        >
          Documentos de {app.name}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ ...numeroTabular, fontSize: 11, color: p.muted }}>
            {docsFilled(app)}/{DOC_KEYS.length} artefatos com link
          </span>
          <ButtonGhost dark={dark} onClick={onOpenApp}>
            Abrir ficha da aplicação
          </ButtonGhost>
        </div>
      </div>

      {/* Subtabela dos artefatos documentais */}
      <div
        style={{
          background: p.surface,
          border: `1px solid ${p.border}`,
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {/* Cabeçalho de colunas só existe onde HÁ colunas: no celular cada
            documento é um card com o próprio rótulo, e a fileira "Documento /
            Link / Situação" seria uma linha morta ocupando altura. */}
        {!narrow && (
          <div
            style={{
              ...rotuloCaixaAlta,
              display: 'grid',
              gridTemplateColumns: '230px 1fr 140px',
              gap: 14,
              padding: '8px 14px',
              background: p.surface2,
              borderBottom: `1px solid ${p.border}`,
              fontSize: 9.5,
              fontWeight: 700,
              color: p.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            <div>Documento</div>
            <div>Link</div>
            <div style={{ textAlign: 'right' }}>Situação</div>
          </div>
        )}
        {docChecks.map((c, i) => (
          <DocRow
            key={c.key}
            check={c}
            dark={dark}
            accent={accent}
            last={i === docChecks.length - 1}
            applicationId={app.id}
          />
        ))}
      </div>

      {/* Critérios de cadastro + resumo de conformidade da aplicação */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: L.panels,
          gap: 12,
          marginTop: 12,
        }}
      >
        <div
          style={{
            background: p.surface,
            border: `1px solid ${p.border}`,
            borderRadius: 8,
            padding: 14,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: p.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 10,
            }}
          >
            Critérios de cadastro
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* No celular o critério empilha: rótulo, valor e só então o selo. */}
            {metaChecks.map((c) => (
              <div
                key={c.key}
                style={{
                  display: 'flex',
                  flexDirection: L.metaDirection,
                  alignItems: L.metaAlign,
                  justifyContent: 'space-between',
                  gap: L.metaGap,
                }}
              >
                <div style={{ minWidth: 0, maxWidth: '100%' }}>
                  <div style={{ fontSize: 12.5, color: p.text, fontWeight: 500 }}>{c.label}</div>
                  <div
                    style={{
                      fontSize: 11,
                      color: c.value ? p.textMuted : p.placeholder,
                      fontFamily: c.value ? fontMono : fontSans,
                      ...L.text,
                    }}
                  >
                    {c.value ?? (c.exempt ? 'Não exigido para esta aplicação' : 'Não informado')}
                  </div>
                </div>
                <StatusPill check={c} dark={dark} />
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            background: p.surface,
            border: `1px solid ${p.border}`,
            borderRadius: 8,
            padding: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: p.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Conformidade desta aplicação
            </div>
            <LevelBadge level={result.level} dark={dark} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 8 }}>
            <div
              style={{
                ...numeroTabular,
                fontSize: 30,
                fontWeight: 600,
                color,
                lineHeight: 1,
                letterSpacing: '-0.03em',
              }}
            >
              {result.pct.toFixed(0)}%
            </div>
            <div style={{ ...numeroTabular, fontSize: 11, color: p.muted, paddingBottom: 3 }}>
              {result.ok}/{result.total} critérios
            </div>
          </div>
          <Bar value={result.ok} total={result.total} color={color} dark={dark} height={6} />
          <div style={{ marginTop: 10 }}>
            {result.missing.length === 0 ? (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: COMPLIANCE_GREEN,
                  fontWeight: 500,
                }}
              >
                <IconCheck size={13} /> Nenhuma pendência neste acervo
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: p.muted, marginBottom: 6 }}>
                  Pendências ({result.missing.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {result.missing.map((c) => (
                    <span
                      key={c.key}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '3px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500,
                        background: gapTone.bg,
                        color: COMPLIANCE_RED,
                        border: `1px solid ${gapTone.border}`,
                      }}
                    >
                      <IconAlert size={11} /> {c.label}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Chrome completo com estado de erro — usado pela página quando /api falha. */
export function ScreenDocumentsError({
  dark,
  accent = NOC_RED,
  role = 'admin',
  onRetry,
}: Readonly<ScreenProps & { role?: Role; onRetry: () => void }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="documents" dark={dark} accent={accent} role={role} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          title="Acervo de documentos"
          subtitle="Documentação por aplicação e percentual de conformidade"
        />
        <div
          style={{
            flex: 1,
            padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              background: p.surface,
              border: `1px solid ${p.border}`,
              borderRadius: 12,
              padding: byViewport(vp, { mobile: 20, tablet: 28, desktop: 28 }),
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 10,
                margin: '0 auto 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: dark ? '#dc262622' : '#fee2e2',
                color: COMPLIANCE_RED,
              }}
            >
              <IconAlert size={20} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: p.text, marginBottom: 8 }}>
              Falha ao carregar o acervo
            </div>
            <div style={{ fontSize: 12.5, color: p.muted, lineHeight: 1.6, marginBottom: 20 }}>
              Não foi possível obter as aplicações e seus documentos. Verifique sua conexão e tente
              novamente.
            </div>
            <button
              type="button"
              onClick={onRetry}
              style={{
                // "Tentar novamente" é a única ação da tela de falha: no celular
                // vai aos 40px do piso.
                height: byViewport(vp, { mobile: 40, tablet: 34, desktop: 34 }),
                padding: '0 16px',
                background: accent,
                color: '#fff',
                border: 'none',
                borderRadius: 7,
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: fontSans,
                cursor: 'pointer',
              }}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Uma aplicação do acervo com a conformidade já calculada. */
type ArchiveRow = { app: App; result: ComplianceResult };

interface ArchiveSummary {
  apps: number;
  docsTotal: number;
  docsPossible: number;
  avg: number;
  complete: number;
}

/** Números dos tiles do topo — tudo derivado do cadastro, nada arredondado. */
function summarize(apps: App[], rows: ArchiveRow[]): ArchiveSummary {
  const docsTotal = apps.reduce((acc, a) => acc + docsFilled(a), 0);
  const avg = rows.length ? rows.reduce((acc, r) => acc + r.result.pct, 0) / rows.length : 0;
  return {
    apps: apps.length,
    docsTotal,
    docsPossible: apps.length * DOC_KEYS.length,
    avg,
    complete: rows.filter((r) => r.result.level === 'total').length,
  };
}

interface ArchiveCounts {
  byLevel: Record<ComplianceLevel, number>;
  byGap: Record<GapFilter, number>;
}

/** Contagem que alimenta os chips: uma passada só sobre as linhas. */
function countFilters(rows: ArchiveRow[]): ArchiveCounts {
  const byLevel: Record<ComplianceLevel, number> = { total: 0, parcial: 0, falha: 0 };
  const byGap: Record<GapFilter, number> = {
    all: rows.length,
    any: 0,
    tech: 0,
    biz: 0,
    api: 0,
    req: 0,
    repo: 0,
  };
  for (const r of rows) {
    byLevel[r.result.level] += 1;
    if (r.result.missing.length > 0) byGap.any += 1;
    for (const k of DOC_KEYS) {
      const c = r.result.checks.find((x) => x.key === k);
      if (c && !c.ok) byGap[k] += 1;
    }
  }
  return { byLevel, byGap };
}

/** Busca livre em nome, identificador, responsável, time e projeto. */
function matchesQuery(app: App, q: string): boolean {
  return (
    !q ||
    [app.name, app.slug, app.owner, app.team, app.project ?? ''].some((f) =>
      String(f).toLowerCase().includes(q),
    )
  );
}

/** Filtro de pendência: qualquer uma, uma doc específica, ou sem filtro. */
function matchesGap(result: ComplianceResult, gap: GapFilter): boolean {
  if (gap === 'all') return true;
  if (gap === 'any') return result.missing.length > 0;
  const c = result.checks.find((x) => x.key === gap);
  return !!c && !c.ok;
}

/** Aplica busca + conformidade + pendência e devolve na ordem escolhida. */
function filterAndSortRows(
  rows: ArchiveRow[],
  q: string,
  level: LevelFilter,
  gap: GapFilter,
  sort: SortMode,
): ArchiveRow[] {
  const list = rows.filter(
    (r) =>
      matchesQuery(r.app, q) &&
      (level === 'all' || r.result.level === level) &&
      matchesGap(r.result, gap),
  );
  return list.sort((a, b) =>
    sort === 'name'
      ? a.app.name.localeCompare(b.app.name, 'pt-BR')
      : a.result.ok - b.result.ok || a.app.name.localeCompare(b.app.name, 'pt-BR'),
  );
}

/** Alterna a presença de um slug no conjunto das linhas expandidas. */
function toggleInSet(current: Set<string>, slug: string): Set<string> {
  const next = new Set(current);
  if (next.has(slug)) next.delete(slug);
  else next.add(slug);
  return next;
}

/** Subtítulo do TopBar: acervo vazio tem frase própria, sem número zerado. */
function archiveSubtitle(totalApps: number, summary: ArchiveSummary): string {
  if (totalApps === 0) return 'Nenhuma aplicação cadastrada ainda';
  const unit = summary.apps === 1 ? 'aplicação' : 'aplicações';
  return `${summary.apps} ${unit} · ${summary.docsTotal} de ${summary.docsPossible} documentos cadastrados · cobertura média ${summary.avg.toFixed(0)}%`;
}

/** Acervo sem nenhuma aplicação: explica o que falta e como começar. */
function EmptyArchive({
  dark,
  accent,
  role,
  onCreate,
}: Readonly<{ dark: boolean; accent: string; role: Role; onCreate: () => void }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        border: `1px dashed ${p.borderStrong}`,
        borderRadius: 12,
        background: p.surface,
        // 56px de respiro vertical no celular jogariam o texto abaixo da dobra.
        padding: byViewport(vp, {
          mobile: '32px 18px',
          tablet: '56px 32px',
          desktop: '56px 32px',
        }),
        textAlign: 'center',
        maxWidth: 620,
        margin: byViewport(vp, {
          mobile: '20px auto 0',
          tablet: '40px auto 0',
          desktop: '40px auto 0',
        }),
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          margin: '0 auto 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: p.surface2,
          border: `1px solid ${p.border}`,
          color: accent,
        }}
      >
        <IconDoc size={24} />
      </div>
      <div
        style={{
          fontSize: 17,
          fontWeight: 600,
          color: p.text,
          letterSpacing: '-0.015em',
          marginBottom: 8,
        }}
      >
        O acervo está vazio
      </div>
      <div
        style={{
          fontSize: 13,
          color: p.muted,
          lineHeight: 1.7,
          maxWidth: 440,
          margin: '0 auto 22px',
        }}
      >
        Cadastre a primeira aplicação com os links de documentação técnica, regra de negócio, API,
        requisitos e repositório. O percentual de conformidade de cada aplicação aparece aqui
        automaticamente.
      </div>
      {role === 'admin' && (
        <button
          type="button"
          onClick={onCreate}
          style={{
            // Único botão do acervo vazio: 36px é abaixo do piso de toque.
            height: byViewport(vp, { mobile: 40, tablet: 36, desktop: 36 }),
            padding: '0 18px',
            background: accent,
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: fontSans,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 0.7 }}>+</span> Cadastrar aplicação
        </button>
      )}
    </div>
  );
}

/**
 * Rótulo da célula, renderizado só no card do celular. Lá o cabeçalho de colunas
 * não existe, e um valor solto ("Fullstack") não diz de qual coluna veio.
 */
function CellLabel({ text, dark, show }: Readonly<{ text: string; dark: boolean; show: boolean }>) {
  const p = makePalette(dark);
  if (!show) return null;
  return (
    <div
      style={{
        ...rotuloCaixaAlta,
        fontSize: 9,
        fontWeight: 700,
        color: p.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: 2,
      }}
    >
      {text}
    </div>
  );
}

/** Linha da tabela principal + o bloco expandido daquela aplicação. */
function DocumentTableRow({
  app,
  result,
  dark,
  accent,
  open,
  last,
  onToggle,
  onOpenApp,
}: Readonly<{
  app: App;
  result: ComplianceResult;
  dark: boolean;
  accent: string;
  open: boolean;
  last: boolean;
  onToggle: () => void;
  onOpenApp: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // A linha inteira continua sendo UM <button> (nunca um div com role="button"):
  // no celular ela só troca a grade de 6 colunas por um card de 3, então o toque
  // que expande e recolhe segue exatamente o mesmo.
  const narrow = vp.mobile;
  const L = narrow ? ROW_NARROW : ROW_WIDE;
  const color = complianceColor(result.pct);
  const openBg = dark ? '#131418' : '#fcfcfb';
  return (
    <div
      style={{
        borderBottom: last ? 'none' : `1px solid ${p.border}`,
        background: open ? openBg : 'transparent',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        title={open ? 'Recolher documentos' : 'Ver documentos'}
        style={{
          display: 'grid',
          gridTemplateColumns: L.columns,
          gap: L.gap,
          width: '100%',
          padding: L.padding,
          alignItems: L.align,
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          color: p.text,
          fontFamily: fontSans,
          fontSize: 12.5,
          cursor: 'pointer',
          transition: 'background 120ms',
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.currentTarget.style.background = p.surface2;
        }}
        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <div
          style={{
            ...L.slot.app,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minWidth: 0,
          }}
        >
          <AppPhoto app={app} size={30} dark={dark} />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: p.text,
                ...L.text,
              }}
            >
              {app.name}
            </div>
            <div
              style={{
                fontSize: 10,
                color: p.muted,
                fontFamily: fontMono,
                ...L.text,
              }}
            >
              {app.slug}
            </div>
          </div>
        </div>
        <div style={{ ...L.slot.type, display: 'flex', alignItems: 'center', gap: 6 }}>
          <AppTypeTag type={app.type} dark={dark} />
        </div>
        <div style={{ ...L.slot.owner, minWidth: 0 }}>
          <CellLabel text="Responsável" dark={dark} show={narrow} />
          <div
            style={{
              fontSize: 12,
              color: app.owner ? p.text : p.placeholder,
              ...L.text,
            }}
          >
            {app.owner || 'Sem responsável'}
          </div>
        </div>
        <div style={{ ...L.slot.team, minWidth: 0 }}>
          <CellLabel text="Time" dark={dark} show={narrow} />
          <div
            style={{
              fontSize: 12,
              color: p.textMuted,
              ...L.text,
            }}
          >
            {app.team}
          </div>
        </div>
        <div style={L.slot.coverage}>
          <CellLabel text="Cobertura documental" dark={dark} show={narrow} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 4,
            }}
          >
            <div style={{ flex: 1 }}>
              <Bar value={result.ok} total={result.total} color={color} dark={dark} />
            </div>
            <div
              style={{
                ...numeroTabular,
                fontSize: 11,
                fontWeight: 700,
                color,
                width: 36,
                textAlign: 'right',
              }}
            >
              {result.pct.toFixed(0)}%
            </div>
          </div>
          <div
            style={{
              ...numeroTabular,
              fontSize: 10,
              color: p.muted,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>
              {result.ok}/{result.total}
            </span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span>
              {docsFilled(app)}/{DOC_KEYS.length} documentos
            </span>
          </div>
        </div>
        <div
          style={{
            ...L.slot.chevron,
            display: 'flex',
            justifyContent: 'flex-end',
            color: open ? accent : p.muted,
          }}
        >
          <span
            style={{
              display: 'flex',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 160ms',
            }}
          >
            <IconChevronR size={15} />
          </span>
        </div>
      </button>

      {open && (
        <ExpandedRow app={app} result={result} dark={dark} accent={accent} onOpenApp={onOpenApp} />
      )}
    </div>
  );
}

export function ScreenDocuments({
  dark,
  accent = NOC_RED,
  apps,
  role = 'admin',
}: Readonly<ScreenProps & { apps: App[]; role?: Role }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState<LevelFilter>('all');
  const [gap, setGap] = useState<GapFilter>('all');
  const [sort, setSort] = useState<SortMode>('coverage');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // Uma passada de conformidade por app; tudo abaixo deriva daqui.
  const rows = useMemo(() => apps.map((a) => ({ app: a, result: complianceOf(a) })), [apps]);

  const summary = useMemo(() => summarize(apps, rows), [apps, rows]);
  const counts = useMemo(() => countFilters(rows), [rows]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => filterAndSortRows(rows, q, level, gap, sort),
    [rows, q, level, gap, sort],
  );

  const filtersActive = !!q || level !== 'all' || gap !== 'all';
  const clearFilters = () => {
    setQuery('');
    setLevel('all');
    setGap('all');
  };

  const toggleRow = (slug: string) => {
    setExpanded((prev) => toggleInSet(prev, slug));
  };

  const allOpen = filtered.length > 0 && filtered.every((r) => expanded.has(r.app.slug));
  const toggleAll = () => {
    setExpanded(allOpen ? new Set() : new Set(filtered.map((r) => r.app.slug)));
  };

  const subtitle = archiveSubtitle(apps.length, summary);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="documents" dark={dark} accent={accent} role={role} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          title="Acervo de documentos"
          subtitle={subtitle}
          right={
            apps.length > 0 ? (
              <ButtonGhost dark={dark} onClick={toggleAll}>
                {allOpen ? 'Recolher tudo' : 'Expandir tudo'}
              </ButtonGhost>
            ) : undefined
          }
        />

        <div
          style={{
            flex: 1,
            padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          {apps.length === 0 ? (
            <EmptyArchive
              dark={dark}
              accent={accent}
              role={role}
              onCreate={() => navigate('/admin/applications/new')}
            />
          ) : (
            <>
              {/* Tiles de resumo — 4 em linha no desktop, 2×2 abaixo dele. Com
                  `minmax(0, 1fr)` no celular porque `1fr` tem piso de conteúdo:
                  era a fileira de tiles que estourava 359px em 320px de tela. */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: byViewport(vp, {
                    mobile: 'repeat(2, minmax(0, 1fr))',
                    tablet: 'repeat(2, 1fr)',
                    desktop: 'repeat(4, 1fr)',
                  }),
                  gap: byViewport(vp, { mobile: 10, tablet: 12, desktop: 14 }),
                  marginBottom: byViewport(vp, { mobile: 14, tablet: 16, desktop: 18 }),
                }}
              >
                <Tile
                  dark={dark}
                  label="Aplicações no acervo"
                  value={String(summary.apps)}
                  sub="registros ativos no inventário"
                  color={accent}
                  icon={<IconDoc size={14} />}
                />
                <Tile
                  dark={dark}
                  label="Documentos cadastrados"
                  value={String(summary.docsTotal)}
                  sub={`de ${summary.docsPossible} links possíveis`}
                  color="#2563eb"
                  icon={<IconExternal size={14} />}
                />
                <Tile
                  dark={dark}
                  label="Cobertura média"
                  value={`${summary.avg.toFixed(0)}%`}
                  sub="média dos 8 critérios por aplicação"
                  color={complianceColor(summary.avg)}
                  icon={<IconCheck size={14} />}
                />
                <Tile
                  dark={dark}
                  label="Documentação completa"
                  value={String(summary.complete)}
                  sub={`de ${summary.apps} em conformidade total`}
                  color={COMPLIANCE_GREEN}
                  icon={<IconCheck size={14} />}
                />
              </div>

              {/* Busca + ordenação */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 10,
                }}
              >
                {/* No celular a busca ocupa a largura toda e, com o `flexWrap`
                    já existente, empurra "Ordenar" para a linha de baixo. Os
                    300px fixos estouravam os 296px úteis de uma tela de 320px. */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    // 44 e não 40: com `box-sizing: border-box` a moldura de 40
                    // menos os 2px de borda deixava 38 para o campo esticado, e a
                    // sonda mediu exatamente `input 308×38` — abaixo do piso de
                    // 40. Mudança de ALTURA só: a largura segue 100% da linha.
                    height: byViewport(vp, { mobile: 44, tablet: 32, desktop: 32 }),
                    padding: '0 10px',
                    background: p.surface,
                    border: `1px solid ${p.border}`,
                    borderRadius: 6,
                    width: byViewport<number | string>(vp, {
                      mobile: '100%',
                      tablet: 300,
                      desktop: 300,
                    }),
                    color: p.muted,
                  }}
                >
                  <IconSearch size={13} />
                  <input
                    value={query}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                    placeholder="Buscar por aplicação, responsável, time ou projeto…"
                    style={{
                      flex: 1,
                      // Esticar o campo no celular dá ao toque os 40px da caixa
                      // inteira; centralizado, a área tocável era só os 18px do
                      // texto (medido pela sonda como alvo pequeno).
                      alignSelf: byViewport(vp, {
                        mobile: 'stretch',
                        tablet: 'auto',
                        desktop: 'auto',
                      }),
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      fontSize: 12,
                      color: p.text,
                      fontFamily: fontSans,
                      minWidth: 0,
                    }}
                  />
                  {!!query && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      title="Limpar busca"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 'none',
                        color: p.muted,
                        cursor: 'pointer',
                        padding: 0,
                        // Alvo de toque de 40px no celular (eram 32, abaixo do
                        // piso); no desktop segue com o tamanho do próprio ícone,
                        // como antes. Cabe na moldura de 44px sem transbordar.
                        width: byViewport<number | string>(vp, {
                          mobile: 40,
                          tablet: 'auto',
                          desktop: 'auto',
                        }),
                        height: byViewport<number | string>(vp, {
                          mobile: 40,
                          tablet: 'auto',
                          desktop: 'auto',
                        }),
                        flexShrink: 0,
                      }}
                    >
                      <IconX size={13} />
                    </button>
                  )}
                </div>

                {/* Empurra "Ordenar" para a direita. No celular ele sai do fluxo:
                    numa fileira que embrulha, um vão elástico deixaria os chips
                    de ordenação colados na borda direita da tela. */}
                <div
                  style={{
                    flex: 1,
                    display: byViewport(vp, { mobile: 'none', tablet: 'block', desktop: 'block' }),
                  }}
                />

                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: p.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  Ordenar
                </span>
                <FilterChip
                  label="Pior cobertura"
                  active={sort === 'coverage'}
                  onClick={() => setSort('coverage')}
                  dark={dark}
                  accent={accent}
                />
                <FilterChip
                  label="Nome"
                  active={sort === 'name'}
                  onClick={() => setSort('name')}
                  dark={dark}
                  accent={accent}
                />
              </div>

              {/* Chips de conformidade e de pendência */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 16,
                }}
              >
                <FilterChip
                  label="Todas"
                  count={rows.length}
                  active={level === 'all'}
                  onClick={() => setLevel('all')}
                  dark={dark}
                  accent={accent}
                />
                <FilterChip
                  label={COMPLIANCE_LEVEL_LABELS.total}
                  count={counts.byLevel.total}
                  active={level === 'total'}
                  onClick={() => setLevel('total')}
                  dark={dark}
                  accent={accent}
                />
                <FilterChip
                  label="Parcial"
                  count={counts.byLevel.parcial}
                  active={level === 'parcial'}
                  onClick={() => setLevel('parcial')}
                  dark={dark}
                  accent={accent}
                />
                <FilterChip
                  label={COMPLIANCE_LEVEL_LABELS.falha}
                  count={counts.byLevel.falha}
                  active={level === 'falha'}
                  onClick={() => setLevel('falha')}
                  dark={dark}
                  accent={accent}
                />
                <div style={{ width: 1, height: 24, background: p.border, margin: '0 4px' }} />
                {GAP_CHIPS.map((c) => (
                  <FilterChip
                    key={c.id}
                    label={c.label}
                    count={counts.byGap[c.id]}
                    active={gap === c.id}
                    onClick={() => setGap(gap === c.id ? 'all' : c.id)}
                    dark={dark}
                    accent={accent}
                  />
                ))}
                {filtersActive && (
                  <>
                    <div style={{ flex: 1 }} />
                    <ButtonGhost dark={dark} onClick={clearFilters}>
                      <IconX size={12} /> Limpar filtros
                    </ButtonGhost>
                  </>
                )}
              </div>

              {/* Tabela: linha por aplicação, expansão in-place com os documentos.
                  No TABLET ela mantém as 6 colunas e ganha rolagem horizontal
                  DELIBERADA — comprimir tudo em 768px apagava nome e responsável
                  em reticências. No celular não há canvas nem rolagem: a linha
                  vira card e a expansão continua acontecendo dentro dele. */}
              <div
                style={{
                  background: p.surface,
                  border: `1px solid ${p.border}`,
                  borderRadius: 10,
                  ...byViewport(vp, {
                    mobile: TABLE_CLIP,
                    tablet: TABLE_SCROLL,
                    desktop: TABLE_CLIP,
                  }),
                }}
              >
                <div
                  style={{
                    minWidth: byViewport(vp, {
                      mobile: 0,
                      tablet: TABLE_MIN_WIDTH,
                      desktop: 0,
                    }),
                  }}
                >
                  {/* Cabeçalho de colunas só onde HÁ colunas: no card do celular
                      cada valor carrega o próprio rótulo (ver `CellLabel`). */}
                  <div
                    style={{
                      ...rotuloCaixaAlta,
                      display: byViewport(vp, {
                        mobile: 'none',
                        tablet: 'grid',
                        desktop: 'grid',
                      }),
                      gridTemplateColumns: GRID,
                      gap: 14,
                      padding: '12px 16px',
                      borderBottom: `1px solid ${p.border}`,
                      background: p.surface2,
                      fontSize: 10,
                      fontWeight: 700,
                      color: p.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    <div>Aplicação</div>
                    <div>Tipo</div>
                    <div>Responsável</div>
                    <div>Time</div>
                    <div>Cobertura documental</div>
                    <div />
                  </div>

                  {filtered.length === 0 ? (
                    <div
                      style={{
                        padding: byViewport(vp, {
                          mobile: '32px 16px',
                          tablet: '40px 24px',
                          desktop: '40px 24px',
                        }),
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: 13, color: p.textMuted, marginBottom: 4 }}>
                        Nenhuma aplicação corresponde aos filtros
                      </div>
                      <div style={{ fontSize: 12, color: p.muted, marginBottom: 16 }}>
                        Ajuste a busca ou remova os filtros de conformidade para ver o acervo
                        completo.
                      </div>
                      <ButtonGhost dark={dark} onClick={clearFilters}>
                        <IconX size={12} /> Limpar filtros
                      </ButtonGhost>
                    </div>
                  ) : (
                    filtered.map(({ app, result }, i) => (
                      <DocumentTableRow
                        key={app.id}
                        app={app}
                        result={result}
                        dark={dark}
                        accent={accent}
                        open={expanded.has(app.slug)}
                        last={i === filtered.length - 1}
                        onToggle={() => toggleRow(app.slug)}
                        onOpenApp={() => navigate(`/app/${app.slug}`)}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Rodapé de contagem — empilha no celular: as duas frases lado a
                  lado em 296px viravam quatro linhas cruzadas. */}
              <div
                style={{
                  ...numeroTabular,
                  display: 'flex',
                  flexDirection: byViewport(vp, {
                    mobile: 'column',
                    tablet: 'row',
                    desktop: 'row',
                  }),
                  alignItems: byViewport(vp, {
                    mobile: 'flex-start',
                    tablet: 'center',
                    desktop: 'center',
                  }),
                  justifyContent: 'space-between',
                  gap: byViewport(vp, { mobile: 4, tablet: 0, desktop: 0 }),
                  marginTop: 14,
                  fontSize: 11,
                  color: p.muted,
                }}
              >
                <span>
                  Mostrando {filtered.length} de {rows.length} aplicações
                </span>
                <span>Apps internos são dispensados de parte dos critérios</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
